import {
  CmpEventPaymentStatusEnum,
  CmpInvoiceKindEnum,
  CmpInvoiceStatusEnum,
} from "../../../../graphql/types";
import { getManageInvoiceDeliverySubline } from "../../Invoicing/ManageInvoices/manageInvoiceDeliverySubline";

type EventInvoiceStatusInput = Readonly<{
  dueAtUtcMilli?: number | null;
  invoiceStatus: CmpInvoiceStatusEnum;
  issuedAtUtcMilli?: number | null;
  latestInvoiceDeliverySentAtUtcMilli?: number | null;
  latestInvoiceDocumentViewedAtUtcMilli?: number | null;
  totalAmountInCents?: number | null;
  invoiceKind?: CmpInvoiceKindEnum | null;
  lineItems?: ReadonlyArray<{ totalAmountInCents: number }> | null;
}>;

/**
 * Staff ticket invoices keep their total only in the pricing snapshot, so the
 * API returns totalAmountInCents as null; their lines still add up to the
 * total (eventSalesAttendeeUpdates.ts). Without a total or lines the invoice
 * is not treated as $0.
 */
const getEventInvoiceTotalInCents = (
  invoice: EventInvoiceStatusInput,
): number | null =>
  invoice.totalAmountInCents ??
  (invoice.lineItems?.length
    ? invoice.lineItems.reduce((sum, line) => sum + line.totalAmountInCents, 0)
    : null);

// Design (21) CMP Dashboard.dc.html:14410: a settled $0 ticket invoice reads
// Complimentary; sponsorship invoices keep Paid.
const isComplimentaryEventInvoice = (invoice?: EventInvoiceStatusInput | null) =>
  !!invoice &&
  invoice.invoiceKind !== CmpInvoiceKindEnum.EventSponsorship &&
  getEventInvoiceTotalInCents(invoice) === 0;

export type EventInvoiceStatusFilter =
  | "all"
  | "draft"
  | "awaiting"
  | "paid"
  | "complimentary"
  | "credited"
  | "voided"
  | "sent"
  | "viewed";
export type EventInvoiceSelectableStatusFilter = Exclude<
  EventInvoiceStatusFilter,
  "all"
>;

export const eventInvoiceStatusFilterOptions: ReadonlyArray<{
  label: string;
  value: EventInvoiceStatusFilter;
}> = [
  { value: "all", label: "All Invoice Statuses" },
  // Design invoiceStatusOptions starts with Draft (Monday 13291850773).
  { value: "draft", label: "Draft" },
  { value: "awaiting", label: "Awaiting Payment" },
  { value: "paid", label: "Paid" },
  // Design (20) CMP Dashboard.dc.html:18630 invoiceStatusOptions.
  { value: "complimentary", label: "Complimentary" },
  { value: "credited", label: "Credited" },
  { value: "voided", label: "Voided" },
  { value: "sent", label: "Sent" },
  { value: "viewed", label: "Viewed" },
];

export const EVENT_INVOICE_COMPLIMENTARY_LABEL = "Complimentary";

export const eventInvoiceSelectableStatusFilterOptions: ReadonlyArray<{
  label: string;
  value: EventInvoiceSelectableStatusFilter;
}> = eventInvoiceStatusFilterOptions
  .filter((option) => option.value !== "all")
  .map((option) => ({
    label: option.label,
    value: option.value as EventInvoiceSelectableStatusFilter,
  }));

export const getEventInvoiceStatusLabel = (
  invoice?: EventInvoiceStatusInput | null,
  _timeZone?: string | null,
  _nowUtcMilli = Date.now(),
): string => {
  switch (invoice?.invoiceStatus) {
    case CmpInvoiceStatusEnum.Draft:
      return "Draft";
    case CmpInvoiceStatusEnum.Paid:
    case CmpInvoiceStatusEnum.Waived:
      return isComplimentaryEventInvoice(invoice)
        ? EVENT_INVOICE_COMPLIMENTARY_LABEL
        : "Paid";
    case CmpInvoiceStatusEnum.Refunded:
    case CmpInvoiceStatusEnum.Credited:
      return "Credited";
    case CmpInvoiceStatusEnum.Void:
      return "Voided";
    case CmpInvoiceStatusEnum.Unpaid:
    case CmpInvoiceStatusEnum.NotInvoiced:
    default:
      return "Awaiting Payment";
  }
};

export const isComplimentaryEventPaymentStatus = (
  status: CmpEventPaymentStatusEnum,
): boolean =>
  status === CmpEventPaymentStatusEnum.NotRequired ||
  status === CmpEventPaymentStatusEnum.Waived;

type EventSaleInvoiceStatusSource = Readonly<{
  invoice?: EventInvoiceStatusInput | null;
  paymentStatus: CmpEventPaymentStatusEnum;
}>;

export const EVENT_SALE_NO_CHARGE_LABEL = "No charge";

/**
 * A free or waived sale with no invoice owes nothing, so it must not read as
 * "Awaiting Payment". A paid sale with no invoice (imported from the old
 * system) reads Paid. Any real invoice keeps its own status label.
 */
export const getEventSaleInvoiceStatusLabel = (
  source: EventSaleInvoiceStatusSource,
  timeZone?: string | null,
  nowUtcMilli = Date.now(),
): string => {
  if (!source.invoice && isComplimentaryEventPaymentStatus(source.paymentStatus)) {
    return EVENT_SALE_NO_CHARGE_LABEL;
  }
  if (!source.invoice && source.paymentStatus === CmpEventPaymentStatusEnum.Paid) {
    return "Paid";
  }
  return getEventInvoiceStatusLabel(source.invoice, timeZone, nowUtcMilli);
};

export const eventSaleMatchesStatusFilters = (
  source: EventSaleInvoiceStatusSource,
  filters: ReadonlyArray<EventInvoiceSelectableStatusFilter>,
  timeZone?: string | null,
  nowUtcMilli = Date.now(),
): boolean => {
  if (source.invoice) {
    return eventInvoiceMatchesStatusFilters(
      source.invoice,
      filters,
      timeZone,
      nowUtcMilli,
    );
  }
  switch (getEventSaleInvoiceStatusLabel(source, timeZone, nowUtcMilli)) {
    // A no-charge sale with no invoice is complimentary too.
    case EVENT_SALE_NO_CHARGE_LABEL:
      return filters.length === 0 || filters.includes("complimentary");
    case "Paid":
      return filters.length === 0 || filters.includes("paid");
    default:
      return eventInvoiceMatchesStatusFilters(
        source.invoice,
        filters,
        timeZone,
        nowUtcMilli,
      );
  }
};

export const eventInvoiceMatchesStatusFilter = (
  invoice: EventInvoiceStatusInput | null | undefined,
  filter: EventInvoiceStatusFilter,
  timeZone?: string | null,
  nowUtcMilli = Date.now(),
) => {
  if (filter === "all") {
    return true;
  }

  if (filter === "sent" || filter === "viewed") {
    return (
      getManageInvoiceDeliverySubline(invoice || {}) ===
      (filter === "sent" ? "Sent" : "Viewed")
    );
  }

  const label = getEventInvoiceStatusLabel(invoice, timeZone, nowUtcMilli);

  return (
    (filter === "draft" && label === "Draft") ||
    (filter === "awaiting" && label === "Awaiting Payment") ||
    (filter === "paid" && label === "Paid") ||
    (filter === "complimentary" && label === EVENT_INVOICE_COMPLIMENTARY_LABEL) ||
    (filter === "credited" && label === "Credited") ||
    (filter === "voided" && label === "Voided")
  );
};

export const eventInvoiceMatchesStatusFilters = (
  invoice: EventInvoiceStatusInput | null | undefined,
  filters: ReadonlyArray<EventInvoiceSelectableStatusFilter>,
  timeZone?: string | null,
  nowUtcMilli = Date.now(),
) =>
  filters.length === 0 ||
  filters.some((filter) =>
    eventInvoiceMatchesStatusFilter(invoice, filter, timeZone, nowUtcMilli),
  );

export type EventInvoiceDocumentMode = "INVOICE" | "PAYMENT_RECEIPT";

export const getEventInvoiceDocumentMode = (
  invoiceStatus?: CmpInvoiceStatusEnum | null,
): EventInvoiceDocumentMode | null => {
  switch (invoiceStatus) {
    case CmpInvoiceStatusEnum.Paid:
    case CmpInvoiceStatusEnum.Draft:
    case CmpInvoiceStatusEnum.Refunded:
    case CmpInvoiceStatusEnum.Credited:
    case CmpInvoiceStatusEnum.Void:
    case CmpInvoiceStatusEnum.Waived:
      return "INVOICE";
    default:
      return null;
  }
};
